import {
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { AiChatbotInputMessageComponent, ChatMessageSubmit } from './ai-chatbot-input-message/ai-chatbot-input-message.component';
import { CommonModule } from '@angular/common';
import { AiApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/ai/ai-api';
import {
  WillyQuotaResponse
} from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/ai/willy-quota.response';
import { AuthApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/auth/auth.api';
import { FileApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/file/file-api';
import {
  UploadFileRequest
} from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/requests/dms/upload-file-request';
import { FileType } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/file-type.enum';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SharedModule } from '../../../ui-components/shared.module';
import { AiChatbotHeaderComponent } from './ai-chatbot-header/ai-chatbot-header.component';
import { PremiumTeaserComponent } from '../../../ui-components/premium-teaser/premium-teaser.component';
import { PremiumToastComponent } from '../../../@toasts/premium-toast/premium-toast.component';
import { readPremiumRefusal } from '../../../@services/front/premium-error.util';
import { readWillyQuota, WillyQuotaView } from './willy-quota.util';

/** Limite gratuite, alignée sur `willy-quota.service.ts` du back. */
const WILLY_FREE_DAILY_LIMIT = 10;
import { PremiumService } from '../../../@services/front/premium.service';
import { firstValueFrom, Subscription } from 'rxjs';
import { StorageService } from '../../../@services/front/storage.service';
import { PosthogService } from '../../../@services/front/posthog.service';
import { WillyChatService } from '../../../@services/front/willy-chat.service';

/** One bubble of the visible thread. Error and welcome bubbles are kept out
 * of the history sent to the backend. */
interface ChatBubble {
  role: 'user' | 'assistant';
  text: string;
  photoUrls?: string[];
  externalFileUids?: string[];
  excludeFromHistory?: boolean;
}

@Component({
    selector: 'vw-ai-chatbot',
    imports: [
        AiChatbotInputMessageComponent,
        CommonModule,
        TranslateModule,
        SharedModule,
        AiChatbotHeaderComponent,
        PremiumTeaserComponent,
        PremiumToastComponent,
    ],
    providers: [
        {
            provide: AiApi,
            useClass: AiApi,
        },
        {
            provide: AuthApi,
            useClass: AuthApi,
        },
        {
            provide: FileApi,
            useClass: FileApi,
        },
    ],
    templateUrl: './ai-chatbot.component.html',
    styleUrl: './ai-chatbot.component.scss'
})
export class AiChatbotComponent implements OnDestroy {
  // Emiting input focus so we can display/hide the menu ai actions to let space for the mobile keyboard
  @Output() inputFocus = new EventEmitter<void>();
  @Output() inputBlur = new EventEmitter<void>();

  @ViewChild(AiChatbotInputMessageComponent) inputMessage: AiChatbotInputMessageComponent;
  @ViewChild('thread') thread: ElementRef<HTMLDivElement>;

  inputIsFocus = false;
  chatSessionId: string | undefined;
  messages: ChatBubble[] = [];
  willyLoading = false;
  isGuest = false;

  readonly premiumService = inject(PremiumService);
  premiumPaywallOpen = false;

  /**
   * Décompte du jour, lu au serveur.
   *
   * `null` tant qu'il n'est pas connu, ou si l'appel échoue : ne pas savoir
   * combien de questions restent ne doit jamais empêcher d'en poser une.
   */
  willyQuota: WillyQuotaResponse | null = null;

  /** Ce que le décompte autorise. Les règles vivent dans `willy-quota.util`. */
  private get quotaView(): WillyQuotaView {
    return readWillyQuota(this.willyQuota, this.premiumService.isPremium());
  }

  /** Décompte discret au-dessus de la saisie. */
  get showQuotaChip(): boolean {
    return this.quotaView.showChip;
  }

  /** Questions encore disponibles aujourd'hui. */
  get quotaRemaining(): number {
    return this.quotaView.remaining;
  }

  /** Le quota du jour est épuisé pour un compte gratuit. */
  get isQuotaExhausted(): boolean {
    return this.quotaView.exhausted;
  }

  /**
   * L'encart Willy+ n'apparait qu'apres deux echanges reels.
   *
   * Proposer l'illimite a quelqu'un qui n'a pas encore pose sa premiere
   * question, c'est vendre avant d'avoir rendu service. Deux reponses suffisent
   * a savoir si Willy est utile — et c'est la que la limite de 10 questions par
   * jour commence a compter.
   */
  get showWillyTeaser(): boolean {
    return (
      this.premiumService.ready() &&
      !this.premiumService.isPremium() &&
      // Quota épuisé : l'encart du mur prend le relais, au-dessus de la saisie.
      // Deux cartes dorées à l'écran diluent l'une et l'autre.
      !this.isQuotaExhausted &&
      this.messages.filter((m) => m.role === 'assistant').length >= 2
    );
  }

  private openChatSubscription: Subscription;

  constructor(
    private aiApi: AiApi,
    private authApi: AuthApi,
    private fileApi: FileApi,
    private translate: TranslateService,
    private posthogService: PosthogService,
    private willyChatService: WillyChatService,
  ) { }

  async ngOnInit() {
    await this.pushWelcomeMessage();

    // Check if user is authenticated without fetching user data
    const token = this.authApi.decodeToken();
    this.isGuest = token['cognito:groups'].includes('GUEST');

    // Un invité n'a pas de compteur : il n'a pas encore de compte.
    if (!this.isGuest) void this.loadQuota();

    // Pre-fill the input when the chat is opened from elsewhere (e.g. plant details)
    this.openChatSubscription = this.willyChatService.openChat$.subscribe((prefillText) => {
      if (prefillText) {
        this.inputMessage?.setMessage(prefillText);
      }
    });
  }

  ngOnDestroy() {
    this.openChatSubscription?.unsubscribe();
    this.revokePhotoUrls();
  }

  // Send the message (and optional photos) to the AI and append the reply to the thread
  async sendMessageToWilly(submit: ChatMessageSubmit) {
    if (this.willyLoading) {
      return;
    }

    // Quota épuisé : inutile d'envoyer la question pour se faire refuser, et
    // d'empiler un second message identique dans le fil. On revérifie tout de
    // même auprès du serveur — le compteur local peut dater d'hier, ou d'un
    // abonnement souscrit entre-temps — et on n'ouvre l'offre que s'il confirme.
    if (this.isQuotaExhausted) {
      await this.loadQuota();
      if (this.isQuotaExhausted) {
        this.premiumPaywallOpen = true;
        return;
      }
    }

    this.posthogService.capture('willy_message_sent');

    // History BEFORE this turn — the new question travels as `text`
    const history = this.messages
      .filter((m) => !m.excludeFromHistory && m.text)
      .map((m) => ({ role: m.role, text: m.text, externalFileUids: m.externalFileUids }))
      .slice(-20);

    const userBubble: ChatBubble = {
      role: 'user',
      text: submit.text,
      photoUrls: submit.photos?.map((photo) => URL.createObjectURL(photo)),
    };
    this.messages.push(userBubble);
    this.willyLoading = true;
    this.scrollToBottom();

    try {
      let externalFileUids: string[] | undefined;
      let text = submit.text;
      if (submit.photos?.length) {
        this.posthogService.capture('willy_message_with_photo', { count: submit.photos.length });
        try {
          externalFileUids = await Promise.all(submit.photos.map((photo) => this.uploadPhoto(photo)));
          userBubble.externalFileUids = externalFileUids;
        } catch {
          // S3 upload failure is not a Willy failure: dedicated message
          userBubble.excludeFromHistory = true;
          await this.pushErrorMessage('willy.photo.error.upload');
          return;
        }
        if (!text || text.length === 0) {
          text = await firstValueFrom(this.translate.get('willy.photo.default-question'));
          userBubble.text = text;
        }
      }
      const willyResponse = await this.aiApi.chatWithWilly({
        text,
        sessionId: this.chatSessionId,
        externalFileUids,
        history,
      });
      // The vision branch has no Bedrock session: keep the current one alive
      this.chatSessionId = willyResponse.sessionId ?? this.chatSessionId;
      this.messages.push({ role: 'assistant', text: willyResponse.text });

      // Le serveur vient de consommer une question : on suit son décompte sans
      // le rappeler. Un aller-retour par message pour un chiffre qu'on connaît
      // déjà serait du gaspillage.
      if (this.willyQuota) this.willyQuota.used += 1;
    } catch (error: any) {
      // A failed turn must not pollute the history (a stale user turn would
      // make the backend skip the personalized context for ever after)
      userBubble.excludeFromHistory = true;

      // Un refus premium n'est PAS une erreur : ni message rouge, ni « oups ».
      // Le quota atteint ouvre l'offre ; l'usage équitable, jamais — la
      // personne a déjà payé pour de l'illimité.
      const refusal = readPremiumRefusal(error);
      if (refusal?.messageKey) {
        const limit = refusal.limit ?? WILLY_FREE_DAILY_LIMIT;
        await this.pushErrorMessage(refusal.messageKey, { limit });

        // Le serveur fait foi : s'il refuse pour quota, le décompte local est en
        // retard. On le cale sur le mur pour que l'encart apparaisse aussitôt.
        if (refusal.code === 'WILLY_QUOTA_EXCEEDED') {
          this.willyQuota = { used: limit, limit, unlimited: false };
          // Et on n'ouvre PAS le paywall par-dessus : l'encart doré au-dessus de
          // la saisie porte désormais l'offre, à demeure. Un plein écran surgi
          // au moment du refus s'ajouterait à la bulle et à l'encart pour dire
          // une troisième fois la même chose. Il s'ouvrira si la personne
          // renvoie une question, ce qui est un choix de sa part.
          return;
        }

        if (refusal.shouldOpenPaywall) {
          this.premiumPaywallOpen = true;
        }
        return;
      }

      let errorKey = 'willy.error.500';
      switch (error.data?.statusCode) {
        case 404:
          errorKey = 'willy.error.400';
          break;
        case 403:
          errorKey = 'willy.error.403';
          break;
        case 429:
          errorKey = 'willy.error.429';
          break;
      }
      await this.pushErrorMessage(errorKey);
    } finally {
      this.willyLoading = false;
      this.scrollToBottom();
    }
  }

  /**
   * Relit le décompte du jour.
   *
   * Un échec laisse `willyQuota` à `null` : ni décompte, ni mur. Le serveur
   * refusera de lui-même le moment venu, avec son message.
   */
  private async loadQuota(): Promise<void> {
    try {
      this.willyQuota = await this.aiApi.getWillyQuota();
    } catch {
      this.willyQuota = null;
    }
  }

  private async uploadPhoto(photo: File): Promise<string> {
    const uploadFileRequest: UploadFileRequest = new UploadFileRequest();
    uploadFileRequest.file = photo;
    uploadFileRequest.fileName = photo.name;
    uploadFileRequest.fileType = photo.type as FileType;
    const uploaded = await this.fileApi.uploadFile(uploadFileRequest);
    return uploaded.externalFileUid;
  }

  // "New conversation": forget the session and start from a blank thread
  async onNewConversation() {
    if (this.willyLoading) {
      return;
    }
    this.posthogService.capture('willy_conversation_reset');
    this.chatSessionId = undefined;
    this.revokePhotoUrls();
    this.messages = [];
    await this.pushWelcomeMessage();
    this.inputMessage?.resetMessage();
  }

  private async pushWelcomeMessage() {
    this.messages.push({
      role: 'assistant',
      text: await firstValueFrom(this.translate.get('willy.hi')),
      excludeFromHistory: true,
    });
  }

  /**
   * Bulle de Willy hors conversation : erreur technique ou refus premium.
   *
   * `excludeFromHistory` est essentiel — un tour rate qui resterait dans
   * l'historique ferait sauter le contexte personnalise a tous les suivants.
   */
  private async pushErrorMessage(
    errorKey: string,
    params?: Record<string, unknown>,
  ) {
    this.messages.push({
      role: 'assistant',
      text: await firstValueFrom(this.translate.get(errorKey, params)),
      excludeFromHistory: true,
    });
  }

  private revokePhotoUrls() {
    this.messages.forEach((m) => m.photoUrls?.forEach((url) => URL.revokeObjectURL(url)));
  }

  private scrollToBottom() {
    setTimeout(() => {
      this.thread?.nativeElement?.scroll({
        top: this.thread.nativeElement.scrollHeight,
        behavior: 'smooth',
      });
    }, 50);
  }

  // Emiting input focus so we can display/hide the menu ai actions to let space for the mobile keyboard
  onInputFocus() {
    this.inputIsFocus = true;
    this.inputFocus.emit();
  }

  // Emiting input blur so we can display/hide the menu ai actions to let space for the mobile keyboard
  onInputBlur() {
    this.inputIsFocus = false;
    this.inputBlur.emit();
  }
}
